import React, { useMemo, useState, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import ReactFlowRenderer from "./ui/ReactFlowRenderer";
import { extractStepsByType } from "./FeatureManager.js";
import "./MBTGraph.css"; // Make sure to create this file

//TODO bug on creating new scenario...
//Think about more robust and UX friendly "test books" management

function extractSteps(scenario) {
  if (!scenario) return [];

  // If steps is already an array of objects {type, text}
  if (scenario.steps && Array.isArray(scenario.steps)) {
    if (typeof scenario.steps[0] === "object" && scenario.steps[0].text) {
      return scenario.steps;
    }
    // If steps are strings, parse the type
    if (typeof scenario.steps[0] === "string") {
      return scenario.steps.map((step) => {
        const match = step.match(/^(Given|When|Then|And|But)\s+(.*)/i);
        if (match) {
          return { type: match[1], text: match[2] };
        }
        return { type: "Step", text: step };
      });
    }
  }

  // Otherwise, parse the description or markdown
  const desc = scenario.description || scenario.title || "";
  // Extract all Gherkin steps on all lines
  const lines = desc
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => /^(Given|When|Then|And|But)\b/i.test(l));
  if (lines.length > 0) {
    return lines.map((line) => {
      const match = line.match(/^(Given|When|Then|And|But)\s+(.*)/i);
      if (match) {
        return { type: match[1], text: match[2] };
      }
      return { type: "Step", text: line };
    });
  }

  // Fallback: split on Gherkin keywords in the description
  const fallback = (desc || "")
    .split(/(?=Given |When |Then |And |But )/g)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const match = line.match(/^(Given|When|Then|And|But)\s+(.*)/i);
      if (match) {
        return { type: match[1], text: match[2] };
      }
      return { type: "Step", text: line };
    });
  return fallback;
}

function getScenarioColor(type) {
  if (type === "automatisé") return "#a5b4fc";
  if (type === "exploratoire") return "#fbbf24";
  if (type === "acceptance") return "#34d399";
  return "#f3f4f6";
}

function getStepColor(step) {
  // Coloring by step type (Given/When/Then)
  if (typeof step === "string") {
    if (step.startsWith("Given") || step.startsWith("Étant donné"))
      return { bg: "#dbeafe", border: "#3b82f6" }; // light blue
    if (step.startsWith("When") || step.startsWith("Quand"))
      return { bg: "#fef3c7", border: "#d97706" }; // yellow
    if (step.startsWith("Then") || step.startsWith("Alors"))
      return { bg: "#dcfce7", border: "#10b981" }; // green
    if (step.startsWith("And") || step.startsWith("Et"))
      return { bg: "#e5e7eb", border: "#6b7280" }; // gray
  }
  return { bg: "#f9fafb", border: "#6366f1" }; // default
}

// Extract the type (Given/When/Then) from a step
function extractStepType(step) {
  if (typeof step !== "string") return "";

  if (step.startsWith("Given") || step.startsWith("Étant donné"))
    return "Given";
  if (step.startsWith("When") || step.startsWith("Quand")) return "When";
  if (step.startsWith("Then") || step.startsWith("Alors")) return "Then";
  if (step.startsWith("And") || step.startsWith("Et")) return "And";

  return "";
}

// Add this function in MBTGraph.js or FeatureManager.js
function formatGherkinStep(step) {
  // Regex to capture Gherkin keywords at the beginning of the string
  const gherkinPattern = /^(Given|When|Then|And|But|Étant donné|Étant donnée|Étant donnés|Étant données|Quand|Alors|Et|Mais)(\s+)(.*)/i;

  if (typeof step !== "string") {
    if (typeof step === "object" && step.type && step.text) {
      // For steps in {type, text} format
      return (
        <div className="gherkin-step border-l-4 pl-3 py-1" style={{
          borderColor:
            step.type.toLowerCase() === "given" ? "#3b82f6" :
              step.type.toLowerCase() === "when" ? "#d97706" :
                step.type.toLowerCase() === "then" ? "#10b981" : "#6b7280"
        }}>
          <span className={`gherkin-keyword gherkin-${step.type.toLowerCase()}`}>
            {step.type}
          </span>
          <span className="gherkin-text">{step.text}</span>
        </div>
      );
    }
    return step;
  }

  const match = step.match(gherkinPattern);
  if (match) {
    const [_, keyword, space, text] = match;

    // Determine class and border color
    let keywordClass = "gherkin-default";
    let borderColor = "#6b7280";

    if (/^(given|étant donn)/i.test(keyword)) {
      keywordClass = "gherkin-given";
      borderColor = "#3b82f6";
    }
    else if (/^(when|quand)/i.test(keyword)) {
      keywordClass = "gherkin-when";
      borderColor = "#d97706";
    }
    else if (/^(then|alors)/i.test(keyword)) {
      keywordClass = "gherkin-then";
      borderColor = "#10b981";
    }
    else if (/^(and|et)/i.test(keyword)) {
      keywordClass = "gherkin-and";
      borderColor = "#6b7280";
    }

    return (
      <div className="gherkin-step border-l-4 pl-3 py-1" style={{ borderColor }}>
        <span className={`gherkin-keyword ${keywordClass}`}>{keyword}</span>
        <span className="gherkin-text">{space + text}</span>
      </div>
    );
  }

  return <span>{step}</span>;
}

const MBTGraph = ({ scenarios, onScenariosChange }) => {
  const { t } = useTranslation();
  const horizontalSpacing = 400;
  const verticalSpacing = 130;

  // States for editing
  const [editingNode, setEditingNode] = useState(null);
  const [editText, setEditText] = useState("");
  const [addingScenario, setAddingScenario] = useState(false);
  const [newScenarioTitle, setNewScenarioTitle] = useState("");
  const [newScenarioType, setNewScenarioType] = useState("acceptance");
  const [newScenarioTags, setNewScenarioTags] = useState("");
  const [newScenarioPriority, setNewScenarioPriority] = useState("medium");
  const [newScenarioCode, setNewScenarioCode] = useState("");
  const [newScenarioDescription, setNewScenarioDescription] = useState("");
  const [addingStep, setAddingStep] = useState(null); // { scenarioIdx, afterStepIdx }
  const [newStepText, setNewStepText] = useState("");

  // Local state for modified scenarios
  const [localScenarios, setLocalScenarios] = useState(scenarios);
  const [activeScenarioDetails, setActiveScenarioDetails] = useState(null);
  // New state for filter
  const [nodeFilter, setNodeFilter] = useState("");

  // Update local scenarios when props change
  React.useEffect(() => {
    setLocalScenarios(scenarios);
  }, [scenarios]);

  // Create a set of all unique nodes in scenarios
  const uniqueNodes = useMemo(() => {
    const nodes = {};
    scenarios.forEach(scenario => {
      if (scenario.nodeName) {
        if (!nodes[scenario.nodeName]) {
          nodes[scenario.nodeName] = 0;
        }
        nodes[scenario.nodeName]++;
      }
    });
    return nodes;
  }, [scenarios]);

  // Filter scenarios based on selected node
  const filteredScenarios = useMemo(() => {
    if (!nodeFilter) return localScenarios;
    return localScenarios.filter(s => s.nodeName === nodeFilter);
  }, [localScenarios, nodeFilter]);

  // Handle node click for editing
  const handleNodeClick = useCallback((eventOrNode, nodeOrEvent) => {
    // Determine which parameter is the node
    const node = nodeOrEvent?.id ? nodeOrEvent : eventOrNode;

    if (!node || !node.id) {
      console.log("Unable to determine node:", {
        eventOrNode,
        nodeOrEvent,
      });
      return;
    }

    // Ignore certain node types
    if (
      node.id.startsWith("start_") ||
      node.id.startsWith("end_") ||
      node.id.startsWith("title_")
    ) {
      return;
    }

    // Extract scenario and step indices
    const [_, scenarioIdx, stepIdx] = node.id.split("_").map(Number);

    // Get raw text of the step from the original scenario
    if (localScenarios[scenarioIdx]) {
      const scenario = localScenarios[scenarioIdx];
      let rawText = "";

      if (scenario.steps && Array.isArray(scenario.steps)) {
        const step = scenario.steps[stepIdx];
        if (typeof step === "object" && step.type && step.text) {
          rawText = `${step.type} ${step.text}`;
        } else {
          rawText = step || "";
        }
      } else {
        const steps = extractSteps(scenario);
        rawText = steps[stepIdx] || "";
      }

      setEditingNode(node);
      setEditText(rawText);
    } else {
      // Fallback just in case
      setEditingNode(node);
      setEditText(node.data?.originalText || "");
    }
  }, [localScenarios]);

  // Save a modified step
  const saveEdit = useCallback(() => {
    if (!editingNode) return;

    // Extract scenario and step index from node ID
    // Format: mbt_${sIdx}_${stepIdx}
    const [_, scenarioIdx, stepIdx] = editingNode.id.split("_").map(Number);

    // Copy scenarios for modification
    const newScenarios = [...localScenarios];

    // Get the scenario to modify
    const scenario = newScenarios[scenarioIdx];
    if (!scenario) return;

    // Keep step type (Given/When/Then) and only replace text
    const originalStepType = extractStepType(editingNode.data.label);
    let updatedStep = editText;

    // If user removed the type, add it back
    if (originalStepType && !extractStepType(editText)) {
      updatedStep = `${originalStepType} ${editText}`;
    }

    // Update according to scenario structure
    if (scenario.steps && Array.isArray(scenario.steps)) {
      // Structured JSON format [{type, text}]
      if (
        typeof scenario.steps[stepIdx] === "object" &&
        scenario.steps[stepIdx].text
      ) {
        const type =
          extractStepType(updatedStep) || originalStepType || "Given";
        scenario.steps[stepIdx].text = updatedStep.replace(
          /^(Given|When|Then|And|Étant donné|Quand|Alors|Et)\s+/,
          ""
        );
        scenario.steps[stepIdx].type = type;
      } else {
        // String array format
        scenario.steps[stepIdx] = updatedStep;
      }
    } else {
      // Text format (use extractSteps)
      let stepsArr = extractSteps(scenario);
      let steps = Array.isArray(stepsArr[0]) ? stepsArr[0] : stepsArr;

      // Modify the step
      if (Array.isArray(steps)) {
        steps[stepIdx] = updatedStep;

        // Update scenario according to its structure
        if (scenario.description) {
          // Text format: rebuild the description
          scenario.description = steps.join("\n");
        }
      }
    }

    // Update local state
    setLocalScenarios(newScenarios);

    // Notify parent if necessary
    if (onScenariosChange) {
      onScenariosChange(newScenarios);
    }

    // Close editor
    setEditingNode(null);
    setEditText("");
  }, [editingNode, editText, localScenarios, onScenariosChange]);

  // Cancel editing
  const cancelEdit = () => {
    setEditingNode(null);
    setEditText("");
  };

  // Add a new scenario
  const handleAddScenario = () => {
    if (!newScenarioTitle.trim()) return;

    const steps = [
      { type: "Given", text: "Une condition initiale" },
      { type: "When", text: "Une action" },
      { type: "Then", text: t('testForms.expectedResult') },
    ];

    const newScenario = {
      title: newScenarioTitle,
      type: newScenarioType, // Use the selected type
      steps: steps,
      description: steps.map((step) => `${step.type} ${step.text}`).join("\n"),
      // Add new properties
      tags: newScenarioTags ? newScenarioTags.split(',').map(tag => tag.trim()) : [],
      priority: newScenarioPriority,
      code: newScenarioType === 'automatisé' ? newScenarioCode : null,
      additionalDescription: newScenarioDescription,
    };

    const newScenarios = [...localScenarios, newScenario];
    setLocalScenarios(newScenarios);

    if (onScenariosChange) {
      onScenariosChange(newScenarios);
    }

    // Reset all states
    setAddingScenario(false);
    setNewScenarioTitle("");
    setNewScenarioType("acceptance");
    setNewScenarioTags("");
    setNewScenarioPriority("medium");
    setNewScenarioCode("");
    setNewScenarioDescription("");
  };

  // Add a step to a scenario
  const handleAddStep = () => {
    if (!addingStep || !newStepText.trim()) return;

    const { scenarioIdx, afterStepIdx } = addingStep;
    const newScenarios = [...localScenarios];
    const scenario = newScenarios[scenarioIdx];

    if (!scenario) return;

    // Determine default step type
    let stepType = "And"; // Default And

    // Determine a more appropriate type based on position
    if (scenario.steps && Array.isArray(scenario.steps)) {
      if (afterStepIdx === -1) {
        // First step = Given
        stepType = "Given";
      } else {
        // Otherwise, look at context
        const prevStepType =
          typeof scenario.steps[afterStepIdx] === "object"
            ? scenario.steps[afterStepIdx].type
            : extractStepType(scenario.steps[afterStepIdx]);

        if (prevStepType === "Given") stepType = "When";
        if (prevStepType === "When") stepType = "Then";
      }

      const newStep = extractStepType(newStepText)
        ? newStepText
        : `${stepType} ${newStepText}`;

      // Insert step at the right place
      if (typeof scenario.steps[0] === "object") {
        // Structured format
        const step = {
          type: extractStepType(newStepText) || stepType,
          text: newStepText.replace(
            /^(Given|When|Then|And|Étant donné|Quand|Alors|Et)\s+/,
            ""
          ),
        };

        scenario.steps.splice(afterStepIdx + 1, 0, step);
      } else {
        // Format texte
        scenario.steps.splice(afterStepIdx + 1, 0, newStep);
      }
    } else {
      // Format texte avec description
      const steps = extractSteps(scenario);
      const newStep = extractStepType(newStepText)
        ? newStepText
        : `${stepType} ${newStepText}`;

      steps.splice(afterStepIdx + 1, 0, newStep);

      if (scenario.description) {
        scenario.description = steps.join("\n");
      }
    }

    setLocalScenarios(newScenarios);

    if (onScenariosChange) {
      onScenariosChange(newScenarios);
    }

    setAddingStep(null);
    setNewStepText("");
  };

  // Delete a step
  const removeStep = (scenarioIdx, stepIdx) => {
    const newScenarios = [...localScenarios];
    const scenario = newScenarios[scenarioIdx];

    if (!scenario) return;

    if (scenario.steps && Array.isArray(scenario.steps)) {
      scenario.steps.splice(stepIdx, 1);

      // Delete scenario if it has no more steps
      if (scenario.steps.length === 0) {
        newScenarios.splice(scenarioIdx, 1);
      }
    } else {
      // Text format
      const steps = extractSteps(scenario);
      steps.splice(stepIdx, 1);

      if (scenario.description) {
        scenario.description = steps.join("\n");
      }

      // Delete scenario if it has no more steps
      if (steps.length === 0) {
        newScenarios.splice(scenarioIdx, 1);
      }
    }

    setLocalScenarios(newScenarios);

    if (onScenariosChange) {
      onScenariosChange(newScenarios);
    }
  };

  // Delete a scenario
  const removeScenario = (scenarioIdx) => {
    if (window.confirm(t('testForms.confirmDelete'))) {
      const newScenarios = [...localScenarios];
      newScenarios.splice(scenarioIdx, 1);

      setLocalScenarios(newScenarios);

      if (onScenariosChange) {
        onScenariosChange(newScenarios);
      }
    }
  };

  // Build nodes and edges
  const { nodes, edges } = useMemo(() => {
    let allNodes = [];
    let allEdges = [];

    filteredScenarios.forEach((scenario, sIdx) => {
      // Create a title node for each scenario
      const titleId = `title_${sIdx}`;
      allNodes.push({
        id: titleId,
        data: {
          label: (
            <div className="flex flex-col items-center w-full">
              <div className="flex items-center justify-between w-full">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveScenarioDetails(scenario);
                  }}
                  className="mbt-button text-blue-600 text-xs px-1"
                  title={t('testForms.viewDetails')}
                >
                  ℹ️
                </button>
                <span className="font-bold">
                  {scenario.title ||
                    (scenario.steps && scenario.steps[0]
                      ? (typeof scenario.steps[0] === "object"
                        ? `${scenario.steps[0].type} ${scenario.steps[0].text}`
                        : scenario.steps[0])
                      : `Scénario ${sIdx + 1}`)}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeScenario(sIdx);
                  }}
                  className="mbt-button mbt-delete-button text-xs px-1"
                  title={t('testForms.deleteScenarioTitle')}
                >
                  ✕
                </button>
              </div>

              {/* Tags */}
              {scenario.tags && scenario.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1 justify-center">
                  {scenario.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Priorité */}
              {scenario.priority && (
                <div className={`text-xs mt-1 px-2 py-0.5 rounded-full ${scenario.priority === 'high' ? 'bg-red-100 text-red-800' :
                  scenario.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                  {scenario.priority === 'high' ? t('testForms.priorities.high') :
                    scenario.priority === 'medium' ? t('testForms.priorities.medium') :
                      t('testForms.priorities.low')}
                </div>
              )}
            </div>
          ),
        },
        position: { x: sIdx * horizontalSpacing, y: 0 },
        style: {
          background: getScenarioColor(scenario.type),
          width: 280,
          maxWidth: 280,
          textAlign: "center",
          padding: "10px",
          fontWeight: "bold",
          borderRadius: 8,
          border: "2px solid #6366f1",
          fontSize: 16,
          boxShadow: "0 2px 5px rgba(0,0,0,0.15)",
        },
      });

      let steps = extractSteps(scenario);
      if (!Array.isArray(steps)) steps = [];

      // Create a start node
      const startId = `start_${sIdx}`;
      allNodes.push({
        id: startId,
        data: {
          label: (
            <div className="flex flex-col items-center w-full">
              <span>▶️ Début</span>
              <button
                onClick={() => {
                  setAddingStep({ scenarioIdx: sIdx, afterStepIdx: -1 });
                  setNewStepText("");
                }}
                className="mbt-button mbt-add-button mbt-centered-button"
                title={t('testForms.addStepAtBeginning')}
              >
                +
              </button>
            </div>
          ),
        },
        position: { x: sIdx * horizontalSpacing, y: 80 },
        style: {
          background: "#f3f4f6",
          border: "2px dashed #6366f1",
          borderRadius: 8,
          padding: 8,
          textAlign: "center",
          fontSize: 14,
          width: 280,
        },
      });

      let lastStepId = startId;
      steps.forEach((step, stepIdx) => {
        const nodeId = `mbt_${sIdx}_${stepIdx}`;
        const colors = getStepColor(step);

        allNodes.push({
          id: nodeId,
          data: {
            label: (
              <div className="flex flex-col w-full">
                <div className="mb-2">{formatGherkinStep(step)}</div>
                <div className="flex justify-center w-full">
                  <div className="mbt-button-group">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setAddingStep({
                          scenarioIdx: sIdx,
                          afterStepIdx: stepIdx,
                        });
                        setNewStepText("");
                      }}
                      className="mbt-button mbt-add-button"
                      title={t('testForms.addStepAfter')}
                    >
                      +
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeStep(sIdx, stepIdx);
                      }}
                      className="mbt-button mbt-delete-button"
                      title={t('testForms.deleteStep')}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            ),
            originalText: step // Store raw text for editing
          },
          position: {
            x: sIdx * horizontalSpacing,
            y: (stepIdx + 1) * verticalSpacing + 80,
          },
          style: {
            background: colors.bg,
            color: "#111827",
            border: `2px solid ${colors.border}`,
            borderRadius: 8,
            fontWeight: 500,
            fontSize: 14,
            padding: 12,
            width: 280,
            maxWidth: 280,
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            cursor: "pointer",
          },
        });

        allEdges.push({
          id: `e_${lastStepId}_${nodeId}`,
          source: lastStepId,
          target: nodeId,
          animated: true,
          style: { stroke: colors.border },
        });

        lastStepId = nodeId;
      });

      // Create an end node
      if (steps.length > 0) {
        const endId = `end_${sIdx}`;
        allNodes.push({
          id: endId,
          data: { label: "🏁 Fin" },
          position: {
            x: sIdx * horizontalSpacing,
            y: (steps.length + 1) * verticalSpacing + 80,
          },
          style: {
            background: "#f3f4f6",
            border: "2px dashed #6366f1",
            borderRadius: 8,
            padding: 8,
            textAlign: "center",
            fontSize: 14,
            width: 100,
          },
        });

        allEdges.push({
          id: `e_${lastStepId}_${endId}`,
          source: lastStepId,
          target: endId,
          animated: true,
        });
      }
    });

    return { nodes: allNodes, edges: allEdges };
  }, [filteredScenarios]);

  if (nodes.length === 0 && !addingScenario) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-500 mb-4">Aucun scénario à visualiser</div>
        {/* Bouton masqué - création gérée par "Créer un test" dans GlobalTestsSection
        <button
          onClick={() => setAddingScenario(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {t('testForms.addScenario')}
        </button>
        */}
      </div>
    );
  }

  // Color legend
  const legend = (
    <div className="legendBloc flex flex-wrap gap-4 mb-4 p-4 bg-white border rounded">
      <div className="text-sm font-semibold">Types d'étapes :</div>
      <div className="flex items-center gap-2">
        <span
          className="inline-block w-4 h-4"
          style={{ background: "#dbeafe", border: "6px solid #3b82f6" }}
        ></span>
        <span>Given (Étant donné)</span>
      </div>
      <div className="flex items-center gap-2">
        <span
          className="inline-block w-4 h-4"
          style={{ background: "#fef3c7", border: "6px solid #d97706" }}
        ></span>
        <span>When (Quand)</span>
      </div>
      <div className="flex items-center gap-2">
        <span
          className="inline-block w-4 h-4"
          style={{ background: "#dcfce7", border: "6px solid #10b981" }}
        ></span>
        <span>Then (Alors)</span>
      </div>
      <div className="flex items-center gap-2">
        <span
          className="inline-block w-4 h-4"
          style={{ background: "#e5e7eb", border: "6px solid #6b7280" }}
        ></span>
        <span>And (Et)</span>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <label className="text-sm">Filtrer par nœud:</label>
        <select
          value={nodeFilter}
          onChange={(e) => setNodeFilter(e.target.value)}
          className="inputNodes py-1 px-2 border rounded text-sm"
        >
          <option value="">Tous les nœuds</option>
          {Object.entries(uniqueNodes).sort().map(([nodeName, count]) => (
            <option key={nodeName} value={nodeName}>
              {nodeName} ({count})
            </option>
          ))}
        </select>
        {nodeFilter && (
          <button
            onClick={() => setNodeFilter("")}
            className="text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
            title={t('titles.clearFilter')}
          >
            ✕
          </button>
        )}
      </div>
      {/* Bouton masqué - création gérée par "Créer un test" dans GlobalTestsSection
      <button
        onClick={() => setAddingScenario(true)}
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
      >
        Ajouter un scénario
      </button>
      */}
    </div>
  );

  // Modal for step editing
  const editModal = editingNode && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full">
        <h3 className="text-lg font-bold mb-4">Modifier l'étape</h3>

        <textarea
          className="w-full p-3 border rounded mb-4 h-32 font-mono text-sm"
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          autoFocus
        />

        <div className="flex justify-end gap-3">
          <button
            onClick={cancelEdit}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
          >
            Annuler
          </button>
          <button
            onClick={saveEdit}
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded"
          >
            Enregistrer
          </button>
        </div>

        <div className="mt-4 text-xs text-gray-500">
          <p>
            Astuce: Commencez par <strong>Given</strong>, <strong>When</strong>,{" "}
            <strong>Then</strong> ou <strong>And</strong> pour définir le type
            d'étape.
          </p>
        </div>
      </div>
    </div>
  );

  // Modal for adding a scenario
  const addScenarioModal = addingScenario && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full">
        <h3 className="text-lg font-bold mb-4">{t('testForms.addScenario')}</h3>

        <div className="space-y-4">
          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('testForms.scenarioTitle')}
            </label>
            <input
              type="text"
              className="w-full p-2 border rounded text-sm"
              value={newScenarioTitle}
              onChange={(e) => setNewScenarioTitle(e.target.value)}
              placeholder={t('testForms.scenarioTitlePlaceholder')}
              autoFocus
            />
          </div>

          {/* Type de scénario */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('testForms.testType')}
            </label>
            <select
              className="w-full p-2 border rounded text-sm"
              value={newScenarioType}
              onChange={(e) => setNewScenarioType(e.target.value)}
            >
              <option value="acceptance">Acceptance</option>
              <option value="automatisé">{t('testForms.automated')}</option>
              <option value="exploratoire">Exploratoire</option>
              <option value="manuel">Manuel</option>
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags (séparés par des virgules)
            </label>
            <input
              type="text"
              className="w-full p-2 border rounded text-sm"
              value={newScenarioTags}
              onChange={(e) => setNewScenarioTags(e.target.value)}
              placeholder={t('placeholders.testTags')}
            />
          </div>

          {/* Priorité */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priorité
            </label>
            <select
              className="w-full p-2 border rounded text-sm"
              value={newScenarioPriority}
              onChange={(e) => setNewScenarioPriority(e.target.value)}
            >
              <option value="high">Haute</option>
              <option value="medium">Moyenne</option>
              <option value="low">Basse</option>
            </select>
          </div>

          {/* Code de test (visible uniquement si le type est "automatisé") */}
          {newScenarioType === "automatisé" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code de test
              </label>
              <textarea
                className="w-full p-2 border rounded font-mono text-sm h-48"
                value={newScenarioCode}
                onChange={(e) => setNewScenarioCode(e.target.value)}
                placeholder={t('placeholders.testCode')}
              />
            </div>
          )}

          {/* Description additionnelle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description additionnelle
            </label>
            <textarea
              className="w-full p-2 border rounded text-sm h-24"
              value={newScenarioDescription}
              onChange={(e) => setNewScenarioDescription(e.target.value)}
              placeholder={t('testForms.contextDetails')}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={() => setAddingScenario(false)}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
          >
            Annuler
          </button>
          <button
            onClick={handleAddScenario}
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded"
          >
            Créer
          </button>
        </div>
      </div>
    </div>
  );

  // Modal for adding a step
  const addStepModal = addingStep && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full">
        <h3 className="text-lg font-bold mb-4">{t('testForms.addNewStep')}</h3>

        <textarea
          className="w-full p-3 border rounded mb-4 h-32 font-mono text-sm"
          value={newStepText}
          onChange={(e) => setNewStepText(e.target.value)}
          placeholder={t('testForms.stepPlaceholderExample')}
          autoFocus
        />

        <div className="flex justify-end gap-3">
          <button
            onClick={() => setAddingStep(null)}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
          >
            Annuler
          </button>
          <button
            onClick={handleAddStep}
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded"
          >
            Ajouter
          </button>
        </div>

        <div className="mt-4 text-xs text-gray-500">
          <p>
            Astuce: Commencez par <strong>Given</strong>, <strong>When</strong>,{" "}
            <strong>Then</strong> ou <strong>And</strong> pour définir le type
            d'étape.
          </p>
        </div>
      </div>
    </div>
  );

  // Modal de détails du scénario
  const scenarioDetailsModal = activeScenarioDetails && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full">
        <h3 className="text-xl font-bold mb-4 flex items-center justify-between">
          <span>{activeScenarioDetails.title}</span>
          <span
            className={`text-sm px-3 py-1 rounded-full ${activeScenarioDetails.type === 'automatisé' ? 'bg-indigo-100 text-indigo-800' :
              activeScenarioDetails.type === 'exploratoire' ? 'bg-amber-100 text-amber-800' :
                'bg-emerald-100 text-emerald-800'
              }`}
          >
            {activeScenarioDetails.type}
          </span>
        </h3>

        <div className="space-y-4">
          {/* Tags */}
          {activeScenarioDetails.tags && activeScenarioDetails.tags.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold">Tags:</h4>
              <div className="flex flex-wrap gap-2 mt-1">
                {activeScenarioDetails.tags.map((tag, idx) => (
                  <span key={idx} className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Priorité */}
          {activeScenarioDetails.priority && (
            <div>
              <h4 className="text-sm font-semibold">Priorité:</h4>
              <div className={`inline-block text-sm mt-1 px-2 py-1 rounded-full ${activeScenarioDetails.priority === 'high' ? 'bg-red-100 text-red-800' :
                activeScenarioDetails.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                {activeScenarioDetails.priority === 'high' ? 'Haute' :
                  activeScenarioDetails.priority === 'medium' ? 'Moyenne' :
                    'Basse'}
              </div>
            </div>
          )}

          {/* Description additionnelle */}
          {activeScenarioDetails.additionalDescription && (
            <div>
              <h4 className="text-sm font-semibold">Description additionnelle:</h4>
              <div className="mt-1 p-3 bg-gray-50 border rounded text-sm">
                {activeScenarioDetails.additionalDescription}
              </div>
            </div>
          )}

          {/* Code de test */}
          {activeScenarioDetails.code && (
            <div>
              <h4 className="text-sm font-semibold">Code de test:</h4>
              <pre className="mt-1 p-3 bg-gray-900 text-gray-100 rounded text-xs overflow-auto max-h-48">
                {activeScenarioDetails.code}
              </pre>
            </div>
          )}

          {/* Étapes */}
          <div>
            <h4 className="text-sm font-semibold">Étapes:</h4>
            <ul className="mt-1 space-y-1">
              {activeScenarioDetails.steps && activeScenarioDetails.steps.map((step, idx) => (
                <li key={idx} className="pl-3 border-l-4 border-gray-300">
                  {formatGherkinStep(
                    typeof step === 'object' ? `${step.type} ${step.text}` : step
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={() => setActiveScenarioDetails(null)}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="my-4">
      {legend}
      <div className="h-[600px] border rounded">
        <ReactFlowRenderer
          nodes={nodes}
          edges={edges}
          fitView
          zoomOnScroll
          panOnDrag
          onNodeClick={handleNodeClick}
        />
      </div>
      {editModal}
      {addScenarioModal}
      {addStepModal}
      {scenarioDetailsModal} {/* Ajouter le modal de détails ici */}
    </div>
  );
};

export default MBTGraph;
